export const PURCHASE_SOURCES = ["direct", "distributor"] as const;
export type PurchaseSource = (typeof PURCHASE_SOURCES)[number];

export const CLAIM_TYPES = ["re-imaging", "repair"] as const;
export type ClaimType = (typeof CLAIM_TYPES)[number];

export type WarrantyClaim = {
  claimType: ClaimType;
  name: string;
  company?: string;
  email: string;
  phone: string;
  serialNumber: string;
  purchasedFrom: PurchaseSource;
  distributorName?: string;
  purchaseDate?: string;
  currentPlatform: string;
  requestedPlatform?: string;
  description?: string;
  pageUrl?: string;
};

export type WarrantyParseResult =
  | { readonly ok: true; readonly discarded: true }
  | { readonly ok: true; readonly data: WarrantyClaim }
  | { readonly ok: false; readonly fieldErrors: Record<string, string> };

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;
const SERIAL_RE = /^[A-Za-z0-9][A-Za-z0-9 ._/-]{2,63}$/;

function asString(value: unknown): string {
  return typeof value === "string" ? value.trim() : "";
}

function clip(value: string, max: number): string {
  return value.slice(0, max);
}

function oneOf<T extends string>(list: readonly T[], value: string): value is T {
  return (list as readonly string[]).includes(value);
}

/** Field rules shared by the API route and the browser form. */
export function validateWarrantyClaim(input: Record<string, unknown>): Record<string, string> {
  const errors: Record<string, string> = {};
  const value = (key: string) => asString(input[key]);

  if (!oneOf(CLAIM_TYPES, value("claimType"))) errors.claimType = "Choose re-imaging or repair.";
  if (!value("name")) errors.name = "Name is required.";
  if (!value("phone")) errors.phone = "Phone is required.";

  const email = value("email");
  if (!email) errors.email = "Email is required.";
  else if (!EMAIL_RE.test(email)) errors.email = "Enter a valid email address.";

  const serial = value("serialNumber");
  if (!serial) errors.serialNumber = "Computer serial number is required.";
  else if (!SERIAL_RE.test(serial)) errors.serialNumber = "Enter the serial number exactly as printed on the board.";

  const source = value("purchasedFrom");
  if (!oneOf(PURCHASE_SOURCES, source)) errors.purchasedFrom = "Tell us where you bought the board.";
  else if (source === "distributor" && !value("distributorName")) {
    errors.distributorName = "Distributor name is required.";
  }

  const purchaseDate = value("purchaseDate");
  if (purchaseDate && !DATE_RE.test(purchaseDate)) errors.purchaseDate = "Choose a valid date.";

  if (!value("currentPlatform")) errors.currentPlatform = "Current platform is required.";
  if (value("claimType") === "re-imaging" && !value("requestedPlatform")) {
    errors.requestedPlatform = "Requested new platform is required.";
  }
  if (value("claimType") === "repair" && !value("description")) {
    errors.description = "Describe the problem with the board.";
  }

  return errors;
}

export function parseWarrantyClaim(input: unknown): WarrantyParseResult {
  if (!input || typeof input !== "object") {
    return { ok: false, fieldErrors: { form: "Please provide valid form data." } };
  }

  const body = input as Record<string, unknown>;

  // Honeypot — bots fill hidden fields; treat as success so they stop retrying.
  if (asString(body.website)) return { ok: true, discarded: true };

  const fieldErrors = validateWarrantyClaim(body);
  if (Object.keys(fieldErrors).length > 0) return { ok: false, fieldErrors };

  const text = (key: string, max: number) => clip(asString(body[key]), max);
  const optional = (key: string, max: number) => text(key, max) || undefined;
  const purchasedFrom = text("purchasedFrom", 20) as PurchaseSource;

  return {
    ok: true,
    data: {
      claimType: text("claimType", 20) as ClaimType,
      name: text("name", 120),
      company: optional("company", 160),
      email: text("email", 180).toLowerCase(),
      phone: text("phone", 40),
      serialNumber: text("serialNumber", 64).toUpperCase(),
      purchasedFrom,
      distributorName: purchasedFrom === "distributor" ? optional("distributorName", 160) : undefined,
      purchaseDate: optional("purchaseDate", 10),
      currentPlatform: text("currentPlatform", 120),
      requestedPlatform: optional("requestedPlatform", 120),
      description: optional("description", 4000),
      pageUrl: optional("pageUrl", 300),
    },
  };
}

/** Human-readable claim reference the customer writes on the package, e.g. WC-20261102-7K3F9Q. */
export function createClaimNumber(now: Date = new Date(), random: () => number = Math.random): string {
  const day = now.toISOString().slice(0, 10).replace(/-/g, "");
  const alphabet = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789";
  const suffix = Array.from({ length: 6 }, () => alphabet[Math.floor(random() * alphabet.length)]).join("");
  return `WC-${day}-${suffix}`;
}

export function claimTypeLabel(type: ClaimType): string {
  return type === "repair" ? "Warranty repair (RMA)" : "Re-imaging / platform change";
}

export function purchaseSourceLabel(claim: Pick<WarrantyClaim, "purchasedFrom" | "distributorName">): string {
  return claim.purchasedFrom === "distributor"
    ? `Distributor${claim.distributorName ? ` — ${claim.distributorName}` : ""}`
    : "Directly from us";
}
